import classNames from "classnames";
import { ProfileItem } from "PFApp/components/profile_item";
import { ProfileSource, ProfileSourceToggle } from "PFApp/components/profile_source_toggle";
import { Select } from "PFComponents/select";
import { getProfileName } from "PFCore/helpers/profile";
import { Collection } from "PFTypes";
import { Option as SelectOption } from "PFTypes/options";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { DisplayOption } from "./display_option";
import css from "./matches_all_resources_selector.module.scss";
import { CostField, Option } from "./matches_all_resources_selector.types";
import { OptionsTableHeader } from "./options_table_header";

type MatchesAllResourcesSelectorProps = {
  profilesSelector: ProfileSource | null;
  onProfilesSelectorChange: (value: ProfileSource) => void;
  onAllResourcesChange: (options: Option[]) => void;
  onMatchesChange: (options: Option[]) => void;
  onShortlistChange?: (options: Option[]) => void;
  fetchAllResourcesOptions: (term?: string) => Promise<Collection<Option[]>>;
  fetchMatchesOptions: (term?: string) => Promise<Collection<Option[]>>;
  fetchShortlistOptions?: (term?: string) => Promise<Collection<Option[]>>;
  allowedSources?: ProfileSource[];
  matchesDisabled?: boolean;
  allResourcesDisabled?: boolean;
  shortlistDisabled?: boolean;
  matchesNotReady?: boolean;
  classes?: { root?: string; select?: string; allResourcesDropdown?: string };
  styles?: { root?: CSSProperties };
  costField?: CostField;
  displayAllResourcesJobTitle?: boolean;
};

const DEFAULT_ALLOWED_SOURCES: ProfileSource[] = [
  ProfileSource.Matches,
  ProfileSource.Shortlist,
  ProfileSource.AllResources
];

export const MatchesAllResourcesSelector = ({
  profilesSelector,
  onProfilesSelectorChange,
  onAllResourcesChange,
  onMatchesChange,
  onShortlistChange,
  fetchAllResourcesOptions,
  fetchMatchesOptions,
  fetchShortlistOptions,
  allowedSources = DEFAULT_ALLOWED_SOURCES,
  matchesDisabled,
  allResourcesDisabled,
  shortlistDisabled,
  matchesNotReady,
  classes = {},
  styles = {},
  costField,
  displayAllResourcesJobTitle
}: MatchesAllResourcesSelectorProps) => {
  const { t } = useTranslation();

  return (
    <div className={classNames(css.root, classes.root)} style={styles.root}>
      {profilesSelector === ProfileSource.AllResources && (
        <Select
          title={t("workforceMember")}
          fetchOptions={async ({ searchTerm }) => {
            const result = await fetchAllResourcesOptions(searchTerm);
            return {
              entries: result.entries.map((profile) => ({
                id: profile.id,
                displayElement: (
                  <ProfileItem profile={profile} displayJobTitle={displayAllResourcesJobTitle} />
                ),
                value: getProfileName(profile),
                original: profile
              })),
              meta: result.meta
            };
          }}
          onChange={({ original }: SelectOption<any>) => onAllResourcesChange([original])}
          value={undefined}
          disabled={allResourcesDisabled}
          dropdownWidth={300}
          className={classes.select}
        />
      )}
      {profilesSelector === ProfileSource.Matches && (
        <Select
          title={t("workforceMember")}
          fetchOptions={async ({ searchTerm }) => {
            const result = await fetchMatchesOptions(searchTerm);
            return {
              entries: result.entries.map((profile) => ({
                id: profile.id,
                displayElement: <DisplayOption option={profile} costField={costField} />,
                value: getProfileName(profile),
                original: profile
              })),
              meta: result.meta
            };
          }}
          onChange={({ original }: SelectOption<any>) => onMatchesChange([original])}
          value={undefined}
          disabled={matchesDisabled}
          dropdownWidth="fit-content"
          className={classes.select}
          noOptionsText={t("profilesSelector.noMatches")}
          dropdownHeaderElement={(options) =>
            options.length > 0 && <OptionsTableHeader costField={costField} />
          }
        />
      )}
      {profilesSelector === ProfileSource.Shortlist && !!fetchShortlistOptions && (
        <Select
          title={t("workforceMember")}
          fetchOptions={async ({ searchTerm }) => {
            const result = await fetchShortlistOptions(searchTerm);
            return {
              entries: result.entries.map((profile) => ({
                id: profile.id,
                displayElement: <DisplayOption option={profile} costField={costField} />,
                value: getProfileName(profile),
                original: profile
              })),
              meta: result.meta
            };
          }}
          onChange={({ original }: SelectOption<any>) => onShortlistChange?.([original])}
          value={undefined}
          disabled={shortlistDisabled}
          dropdownWidth="fit-content"
          className={classes.select}
          noOptionsText={t("profilesSelector.noMatches")}
          dropdownHeaderElement={(options) =>
            options.length > 0 && <OptionsTableHeader costField={costField} />
          }
        />
      )}
      <ProfileSourceToggle
        value={profilesSelector}
        onChange={onProfilesSelectorChange}
        allowedSources={allowedSources}
        disabledSources={{
          [ProfileSource.Matches]: {
            disabled: !!matchesDisabled,
            tooltip: matchesNotReady ? t("profilesSelector.matchesNotReady") : undefined
          },
          [ProfileSource.AllResources]: { disabled: !!allResourcesDisabled },
          [ProfileSource.Shortlist]: {
            disabled: !!shortlistDisabled,
            tooltip: shortlistDisabled ? t("profilesSelector.shortlistDisabledNoShortlists") : undefined
          }
        }}
        fluid
        className={css.profileSourceToggle}
      />
    </div>
  );
};
